import { DataAccess } from "../../dataaccess/data.access";
import { S25Util } from "../../util/s25-util";

export function createChatbotService<ConfigKey extends ServiceConfig>(
    key: ConfigKey,
): S25ChatbotService<ServiceConfigMap[ConfigKey]> {
    return new S25ChatbotService<ServiceConfigMap[ConfigKey]>(key);
}

export class S25ChatbotService<TResponse> {
    private config: ChatbotConfig<TResponse>;

    constructor(serviceKey: ServiceConfig) {
        this.config = CHATBOT_CONFIGS[serviceKey] as ChatbotConfig<TResponse>;
    }

    public async sendMessage(inputText: string): Promise<TResponse> {
        const payload = { message: inputText };
        const [response, error] = await S25Util.Maybe(DataAccess.post<TResponse>(this.config.requestUrl, payload));
        if (error) {
            return Promise.reject(error);
        }
        return this.config.parseResponse(response);
    }

    public async sendMessageThread(thread: Array<Message>): Promise<TResponse> {
        const payload = { messages: thread };
        const [response, error] = await S25Util.Maybe(DataAccess.post<TResponse>(this.config.requestUrl, payload));
        if (error) {
            return Promise.reject(error);
        }
        return response;
    }
}

export type Message = {
    role: "assistant" | "user";
    content: string;
};

// #region Response Types
export type AiExpressSchedulingResponse = {
    chatResponse: string;
    eventName: string;
    startDate: string;
    endDate: string;
    maximumRoomCapacityNeeded: number;
};

export type AiAdvancedSearchResponse = {
    queryGrouping: string;
    matchingResponse?: {
        termMatches: Array<{
            term: string;
            confidenceNumber: number;
            matches: Array<{ itemId: number; itemName: string }>;
        }>;
    };
};

export type AiPricingFormulaResponse = {
    solution: Array<{
        time_range: string;
        duration_range: string;
        formula: string;
        explanation: string;
        variables_used?: Array<{
            variable?: string;
            details?: string;
        }>;
    }>;
};
// #endregion

// #region Service Configs
export type ServiceConfig = "advancedSearch" | "expressScheduling" | "pricingFormula";

export interface ServiceConfigMap {
    expressScheduling: AiExpressSchedulingResponse;
    advancedSearch: AiAdvancedSearchResponse;
    pricingFormula: AiPricingFormulaResponse;
}

type ChatbotConfig<TResponse> = {
    requestUrl: string;
    parseResponse: (raw: any) => TResponse;
};

const CHATBOT_CONFIGS = {
    expressScheduling: {
        requestUrl: "/ai/v2/request.json",
        parseResponse: (raw: any): AiExpressSchedulingResponse => raw,
    } as ChatbotConfig<AiExpressSchedulingResponse>,

    advancedSearch: {
        requestUrl: "/ai/search/request.json",
        parseResponse: (raw: any): AiAdvancedSearchResponse => raw,
    } as ChatbotConfig<AiAdvancedSearchResponse>,

    pricingFormula: {
        requestUrl: "/ai/pricingFormula/request.json",
        parseResponse: (raw: any): AiPricingFormulaResponse => raw,
    } as ChatbotConfig<AiPricingFormulaResponse>,
};
// #endregion
